import { useEffect, useState } from 'react';
import { DeviceMobileIcon, CopyIcon, CheckIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import { Disclosure } from '@/components/ui/disclosure';
import { QrCode } from '@/components/ui/qr-code';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/cms/ui/dialog';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { useShareLink } from '@/hooks/useShareLink';
import { buildAppDeepLink } from '@/lib/buildAppDeepLink';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { TestInMobileAppInstructions } from './test-in-mobile-app-instructions';

/** `url` with the scene test token the Test QR phone follows the scene's changes with (lureo-test). */
function withTestToken(url: string, token: string): string {
  const hashAt = url.indexOf('#');
  const base = hashAt < 0 ? url : url.slice(0, hashAt);
  const hash = hashAt < 0 ? '' : url.slice(hashAt);
  return `${base}${base.includes('?') ? '&' : '?'}lureo-test=${encodeURIComponent(token)}${hash}`;
}

export type TestInstructionsSource =
  | 'projects_page'
  | 'story_edit_page'
  | 'pre_publish_check'
  | 'editor_qr';

interface TestInMobileAppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Noun used in the copy. */
  noun?: 'story' | 'project';
  /** Where the dialog was opened from, recorded for analytics. */
  source: TestInstructionsSource;
  /** Optional ids for analytics correlation. */
  projectId?: string;
  sceneInstanceId?: string;
}

export function TestInMobileAppDialog({
  open,
  onOpenChange,
  noun = 'story',
  source,
  projectId,
  sceneInstanceId,
}: TestInMobileAppDialogProps) {
  const services = useServices();
  const [copied, setCopied] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  // A story is tested through its share link (share links 3.2b); only a project-level test still
  // uses the legacy project deep link, because a project has no link of its own
  const shareLink = useShareLink(open && sceneInstanceId ? sceneInstanceId : null);

  // A scene test token lets the phone follow the scene's changes live; without one the link still opens it
  const [test, setTest] = useState<{ sceneId: string; token: string } | null>(null);
  const testToken = open && test && test.sceneId === sceneInstanceId ? test.token : null;
  useEffect(() => {
    if (!open || !sceneInstanceId) return;
    let cancelled = false;
    void toResult(v1.POST('/api/v1/scenes/{sceneUniqueId}/test-sessions', {
      params: { path: { sceneUniqueId: sceneInstanceId } },
      body: { uniqueId: crypto.randomUUID() },
    })).then(({ data, error }) => {
      if (cancelled) return;
      if (error) console.warn('[TestInMobileApp] no scene test token; the link opens the scene without live changes', error);
      else setTest({ sceneId: sceneInstanceId, token: data.token });
    });
    return () => { cancelled = true; };
  }, [open, sceneInstanceId]);

  const creatorDeepLink = sceneInstanceId
    ? (shareLink.url && testToken ? withTestToken(shareLink.url, testToken) : shareLink.url)
    : projectId
      ? buildAppDeepLink({ projectInstanceId: projectId, isCreator: true })
      : null;

  const handleCopy = async () => {
    if (!creatorDeepLink) return;
    try {
      await navigator.clipboard.writeText(creatorDeepLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard access denied — silently ignore
    }
  };

  useEffect(() => {
    if (!open) return;
    services.analytics.trackLureoTestInstructionsOpened({
      source,
      project_id: projectId,
      scene_instance_id: sceneInstanceId,
    });
  }, [open, source, projectId, sceneInstanceId, services.analytics]);

  if (!open) return null;

  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DeviceMobileIcon className="h-6 w-6 text-lureo-ink" weight="regular" />
            <DialogTitle>Test your {noun} in the Lureo mobile app</DialogTitle>
          </div>
        </DialogHeader>

        <DialogBody>
          {creatorDeepLink && (
            <div className="flex flex-col items-center space-y-6">
              <Disclosure className="w-full" label="How to test app experiences" open={showHowTo} onOpenChange={setShowHowTo}>
                <div className="mt-2 pb-2">
                  <TestInMobileAppInstructions noun={noun} />
                </div>
              </Disclosure>
              <div className="w-full space-y-2 text-center">
                <p className="text-lureo-14 text-lureo-ink-subtle leading-5">
                  Just scan to browse your project in the Lureo app.
                </p>
              </div>
              <QrCode value={creatorDeepLink} size={240} fluid frame="lg" />
              <div className="flex items-center gap-3 w-full justify-center">
                <LureoButton
                  variant="outline-3"
                  onClick={handleCopy}
                  className="h-9 px-5 rounded-lureo-radius-button-pill"
                  tooltipContent={copied ? 'Copied!' : 'Copy link'}
                >
                  {copied
                    ? <CheckIcon className="h-4 w-4 text-lureo-success-ink" />
                    : <CopyIcon className="h-4 w-4" />
                  }
                  Copy
                </LureoButton>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <p className="text-lureo-12 text-lureo-ink-subtle leading-4">Need help?</p>
            <LureoButton
              variant="outline-2"
              suppressTooltip
              onClick={() => window.open('https://discord.gg/ZrthQXgp5a', '_blank', 'noopener,noreferrer')}
              className="h-7 rounded-lureo-radius-button-pill bg-lureo-surface px-3 text-lureo-12 font-medium"
            >
              Join our Discord
            </LureoButton>
          </div>
        </DialogBody>

        <DialogFooter>
          <LureoButton
            variant="secondary"
            suppressTooltip
            onClick={handleClose}
            className="h-9 px-4 rounded-lureo-radius-button-pill"
          >
            Got it
          </LureoButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
